// Social Media Management System Types

// =====================================================
// Platform Types
// =====================================================

export type SocialPlatform = 'instagram' | 'facebook' | 'linkedin' | 'tiktok';

export type SocialAccountStatus = 'pending' | 'active' | 'expired' | 'disconnected';

export type SocialPostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';

export type SocialMessageType = 'text' | 'image' | 'video' | 'story_reply' | 'story_mention' | 'post_comment' | 'reaction';

export type SocialMessageStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | 'received';

export type AutoReplyTriggerType = 'keyword' | 'first_message' | 'out_of_hours' | 'all';

// =====================================================
// Database Entity Types
// =====================================================

export interface SocialAccount {
  id: string;
  business_id: string;
  platform: SocialPlatform;
  platform_account_id: string;
  account_name: string | null;
  account_username: string | null;
  account_avatar_url: string | null;
  access_token: string;
  refresh_token: string | null;
  token_expires_at: string | null;
  page_id: string | null;
  page_access_token: string | null;
  status: SocialAccountStatus;
  connected_at: string | null;
  last_sync_at: string | null;
  webhook_verify_token: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  business_id: string;
  content: string;
  media_urls: string[];
  media_types: string[];
  target_platforms: SocialPlatform[];
  platform_overrides: PlatformOverrides;
  status: SocialPostStatus;
  scheduled_for: string | null;
  published_at: string | null;
  publish_results: PublishResults;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialContact {
  id: string;
  business_id: string;
  platform: SocialPlatform;
  platform_user_id: string;
  username: string | null;
  display_name: string | null;
  profile_picture_url: string | null;
  customer_id: string | null;
  contact_id: string | null;
  last_inbound_at: string | null;
  is_follower: boolean;
  is_blocked: boolean;
  notes: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface SocialMessage {
  id: string;
  business_id: string;
  social_account_id: string;
  contact_id: string;
  platform_message_id: string | null;
  thread_id: string | null;
  direction: 'inbound' | 'outbound';
  message_type: SocialMessageType;
  content: string | null;
  media_url: string | null;
  media_mime_type: string | null;
  post_id: string | null;
  status: SocialMessageStatus;
  error_message: string | null;
  is_auto_reply: boolean;
  auto_reply_rule_id: string | null;
  sent_by_user_id: string | null;
  sent_at: string;
  delivered_at: string | null;
  read_at: string | null;
  created_at: string;
}

export interface SocialAutoReplyRule {
  id: string;
  business_id: string;
  name: string;
  is_active: boolean;
  priority: number;
  trigger_type: AutoReplyTriggerType;
  trigger_config: AutoReplyTriggerConfig;
  platforms: SocialPlatform[];
  response_type: 'text' | 'template';
  response_content: string;
  response_delay_seconds: number;
  max_triggers_per_contact_per_day: number;
  active_schedule: ActiveSchedule;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialAnalyticsSnapshot {
  id: string;
  business_id: string;
  social_account_id: string;
  snapshot_date: string;
  followers_count: number;
  followers_gained: number;
  followers_lost: number;
  impressions: number;
  reach: number;
  engagement_count: number;
  likes_count: number;
  comments_count: number;
  shares_count: number;
  saves_count: number;
  posts_published: number;
  stories_published: number;
  messages_received: number;
  messages_sent: number;
  avg_response_time_minutes: number | null;
  engagement_rate: number | null;
  created_at: string;
}

export interface SocialPostAnalytics {
  id: string;
  social_post_id: string;
  platform: SocialPlatform;
  platform_post_id: string | null;
  impressions: number;
  reach: number;
  likes_count: number;
  comments_count: number;
  shares_count: number;
  saves_count: number;
  clicks_count: number;
  video_views: number;
  engagement_rate: number | null;
  updated_at: string;
}

// =====================================================
// JSON Field Types
// =====================================================

export interface PlatformOverrides {
  instagram?: PlatformOverride;
  facebook?: PlatformOverride;
  linkedin?: PlatformOverride;
  tiktok?: PlatformOverride;
}

export interface PlatformOverride {
  content?: string;
  hashtags?: string[];
  mentions?: string[];
  first_comment?: string;
}

export interface PublishResults {
  instagram?: PlatformPublishResult;
  facebook?: PlatformPublishResult;
  linkedin?: PlatformPublishResult;
  tiktok?: PlatformPublishResult;
}

export interface PlatformPublishResult {
  post_id?: string;
  status: 'published' | 'failed';
  url?: string;
  error?: string;
}

export interface AutoReplyTriggerConfig {
  keywords?: string[];
  match_type?: 'contains' | 'exact' | 'starts_with';
  case_sensitive?: boolean;
}

export interface ActiveSchedule {
  days?: number[]; // 0-6, Sunday = 0
  start_time?: string; // HH:mm format
  end_time?: string; // HH:mm format
  timezone?: string;
}

// =====================================================
// Extended Types (with computed fields)
// =====================================================

export interface SocialContactWithDetails extends SocialContact {
  unread_count: number;
  last_message?: SocialMessage;
  account?: SocialAccount;
}

export interface SocialPostWithAnalytics extends SocialPost {
  analytics?: SocialPostAnalytics[];
  total_engagement?: number;
}

export interface SocialAccountWithStats extends SocialAccount {
  followers_count?: number;
  posts_count?: number;
  latest_analytics?: SocialAnalyticsSnapshot;
}

// =====================================================
// API Request/Response Types
// =====================================================

export interface ConnectSocialAccountRequest {
  business_id: string;
  platform: SocialPlatform;
  access_token: string;
  refresh_token?: string;
  token_expires_at?: string;
  page_id?: string;
  page_access_token?: string;
}

export interface CreateSocialPostRequest {
  business_id: string;
  content: string;
  media_urls?: string[];
  media_types?: string[];
  target_platforms: SocialPlatform[];
  platform_overrides?: PlatformOverrides;
  scheduled_for?: string;
  publish_now?: boolean;
}

export interface UpdateSocialPostRequest {
  content?: string;
  media_urls?: string[];
  media_types?: string[];
  target_platforms?: SocialPlatform[];
  platform_overrides?: PlatformOverrides;
  scheduled_for?: string;
  status?: SocialPostStatus;
}

export interface SendSocialMessageRequest {
  business_id: string;
  contact_id: string;
  content: string;
  message_type?: SocialMessageType;
  media_url?: string;
}

export interface CreateAutoReplyRuleRequest {
  business_id: string;
  name: string;
  trigger_type: AutoReplyTriggerType;
  trigger_config?: AutoReplyTriggerConfig;
  platforms?: SocialPlatform[];
  response_type?: 'text' | 'template';
  response_content: string;
  response_delay_seconds?: number;
  max_triggers_per_contact_per_day?: number;
  active_schedule?: ActiveSchedule;
}

// =====================================================
// OAuth Types
// =====================================================

export interface OAuthState {
  business_id: string;
  platform: SocialPlatform;
  redirect_uri: string;
  nonce: string;
}

export interface OAuthCallbackParams {
  code: string;
  state: string;
}

export interface MetaOAuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface MetaLongLivedTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface MetaPageAccessTokenResponse {
  access_token: string;
  category: string;
  category_list: { id: string; name: string }[];
  name: string;
  id: string;
  tasks: string[];
}

export interface LinkedInOAuthTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token?: string;
  refresh_token_expires_in?: number;
  scope: string;
}

export interface TikTokOAuthTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  refresh_expires_in: number;
  open_id: string;
  scope: string;
}

// =====================================================
// Webhook Types
// =====================================================

export interface MetaWebhookPayload {
  object: 'instagram' | 'page';
  entry: MetaWebhookEntry[];
}

export interface MetaWebhookEntry {
  id: string;
  time: number;
  changes?: MetaWebhookChange[];
  messaging?: MetaMessagingEvent[];
}

export interface MetaWebhookChange {
  field: string;
  value: Record<string, unknown>;
}

export interface MetaMessagingEvent {
  sender: { id: string };
  recipient: { id: string };
  timestamp: number;
  message?: {
    mid: string;
    text?: string;
    attachments?: MetaAttachment[];
    reply_to?: { mid: string };
    is_echo?: boolean;
  };
  reaction?: {
    mid: string;
    action: 'react' | 'unreact';
    reaction?: string;
  };
  read?: {
    watermark: number;
  };
}

export interface MetaAttachment {
  type: 'image' | 'video' | 'audio' | 'file';
  payload: {
    url: string;
  };
}

// =====================================================
// Analytics Types
// =====================================================

export interface AnalyticsSummary {
  total_followers: number;
  followers_change: number;
  followers_change_percent: number;
  total_engagement: number;
  engagement_change: number;
  engagement_change_percent: number;
  total_posts: number;
  avg_engagement_rate: number;
  total_messages_received: number;
  total_messages_sent: number;
}

export interface AnalyticsTimeSeriesData {
  date: string;
  followers: number;
  engagement: number;
  impressions: number;
  reach: number;
}

export interface TopPost {
  post: SocialPost;
  analytics: SocialPostAnalytics;
  platform: SocialPlatform;
}

// =====================================================
// Hook Return Types
// =====================================================

export interface UseSocialAccountsResult {
  accounts: SocialAccount[];
  isLoading: boolean;
  error: Error | null;
  connectAccount: (request: ConnectSocialAccountRequest) => Promise<SocialAccount>;
  disconnectAccount: (accountId: string) => Promise<void>;
  refreshTokens: (accountId: string) => Promise<void>;
  refresh: () => void;
}

export interface UseSocialPostsResult {
  posts: SocialPost[];
  isLoading: boolean;
  error: Error | null;
  createPost: (request: CreateSocialPostRequest) => Promise<SocialPost>;
  updatePost: (postId: string, request: UpdateSocialPostRequest) => Promise<SocialPost>;
  deletePost: (postId: string) => Promise<void>;
  publishNow: (postId: string) => Promise<void>;
  refresh: () => void;
}

export interface UseSocialInboxResult {
  conversations: SocialContactWithDetails[];
  isLoading: boolean;
  error: Error | null;
  selectedConversation: SocialContactWithDetails | null;
  selectConversation: (contactId: string | null) => void;
  markAsRead: (contactId: string) => Promise<void>;
  blockContact: (contactId: string) => Promise<void>;
  unblockContact: (contactId: string) => Promise<void>;
  refresh: () => void;
  filters: SocialInboxFilters;
  updateFilters: (filters: Partial<SocialInboxFilters>) => void;
}

export interface UseSocialMessagesResult {
  messages: SocialMessage[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  sendMessage: (content: string) => Promise<void>;
  markAsRead: () => Promise<void>;
  refresh: () => void;
}

export interface UseSocialAnalyticsResult {
  summary: AnalyticsSummary | null;
  timeSeries: AnalyticsTimeSeriesData[];
  topPosts: TopPost[];
  isLoading: boolean;
  error: Error | null;
  dateRange: { from: Date; to: Date };
  setDateRange: (from: Date, to: Date) => void;
  selectedPlatforms: SocialPlatform[];
  setSelectedPlatforms: (platforms: SocialPlatform[]) => void;
  refresh: () => void;
}

export interface UseAutoReplyRulesResult {
  rules: SocialAutoReplyRule[];
  isLoading: boolean;
  error: Error | null;
  createRule: (request: CreateAutoReplyRuleRequest) => Promise<SocialAutoReplyRule>;
  updateRule: (ruleId: string, updates: Partial<SocialAutoReplyRule>) => Promise<SocialAutoReplyRule>;
  deleteRule: (ruleId: string) => Promise<void>;
  toggleRule: (ruleId: string, isActive: boolean) => Promise<void>;
  refresh: () => void;
}

// =====================================================
// UI Component Types
// =====================================================

export interface PlatformInfo {
  id: SocialPlatform;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  maxContentLength: number;
  supportsImages: boolean;
  supportsVideos: boolean;
  supportsCarousel: boolean;
  supportsDMs: boolean;
}

export const PLATFORM_INFO: Record<SocialPlatform, PlatformInfo> = {
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    icon: 'Instagram',
    color: '#E1306C',
    bgColor: '#FCE4EC',
    maxContentLength: 2200,
    supportsImages: true,
    supportsVideos: true,
    supportsCarousel: true,
    supportsDMs: true,
  },
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    icon: 'Facebook',
    color: '#1877F2',
    bgColor: '#E3F2FD',
    maxContentLength: 63206,
    supportsImages: true,
    supportsVideos: true,
    supportsCarousel: true,
    supportsDMs: true,
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: 'Linkedin',
    color: '#0A66C2',
    bgColor: '#E8F4FD',
    maxContentLength: 3000,
    supportsImages: true,
    supportsVideos: true,
    supportsCarousel: true,
    supportsDMs: false, // Limited API access
  },
  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'Video',
    color: '#000000',
    bgColor: '#F5F5F5',
    maxContentLength: 2200,
    supportsImages: false,
    supportsVideos: true,
    supportsCarousel: false,
    supportsDMs: false, // No API access
  },
};

// =====================================================
// Filter Types
// =====================================================

export interface SocialInboxFilters {
  platform?: SocialPlatform;
  unreadOnly?: boolean;
  search?: string;
}

export interface SocialPostFilters {
  status?: SocialPostStatus;
  platform?: SocialPlatform;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface SocialAnalyticsFilters {
  platforms: SocialPlatform[];
  dateFrom: string;
  dateTo: string;
}
